import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Plus, Search } from 'lucide-react';
import { useCompany } from '../context/CompanyContext';
import { formatCurrency, formatRelativeTime, cn } from '../utils/formatters';
import { CardSkeleton } from '../components/ui/LoadingScreen';
import { input } from '../styles/components';

export function BudgetsPage() {
  const { budgets, clients, isLoading } = useCompany();
  const [search, setSearch] = useState('');

  const budgetStatusConfig: Record<string, { label: string; pill: string }> = {
    draft: { label: 'Borrador', pill: '' },
    sent: { label: 'Enviado', pill: 'b2b-pill--warn' },
    accepted: { label: 'Aceptado', pill: 'b2b-pill--ok' },
    rejected: { label: 'Rechazado', pill: 'b2b-pill--danger' },
    expired: { label: 'Caducado', pill: '' },
  };

  const filteredBudgets = budgets.filter(b =>
    b.number.toLowerCase().includes(search.toLowerCase()) ||
    (clients.find(c => c.id === b.clientId)?.name.toLowerCase().includes(search.toLowerCase()) ?? false)
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-extrabold tracking-tight">Presupuestos</h1>
        <div className="grid gap-4">
          {[1, 2, 3].map(i => <CardSkeleton key={i} />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div className="space-y-1">
          <span className="b2b-eyebrow">Presupuestos</span>
          <h1 className="text-3xl font-extrabold tracking-tight">Presupuestos</h1>
          <p className="b2b-muted">Genera presupuestos profesionales con logo y firma</p>
        </div>
        <Link to="/budgets/new" className="b2b-btn b2b-btn--primary">
          <Plus className="w-4 h-4" />
          Nuevo Presupuesto
        </Link>
      </div>

      <div className="b2b-panel">
        <div className="b2b-panel__head">
          <h2 className="text-lg font-semibold">Listado</h2>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 b2b-muted" />
            <input
              type="text"
              placeholder="Buscar..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className={cn(input, 'pl-9 w-48')}
            />
          </div>
        </div>

        {filteredBudgets.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <h3 className="text-lg font-semibold mb-4">No hay presupuestos</h3>
            <Link to="/budgets/new" className="b2b-btn b2b-btn--primary">
              <Plus className="w-4 h-4" /> Crear presupuesto
            </Link>
          </div>
        ) : (
          filteredBudgets.map(budget => {
            const client = clients.find(c => c.id === budget.clientId);
            const config = budgetStatusConfig[budget.status] || budgetStatusConfig.draft;
            return (
              <Link key={budget.id} to={`/budgets/${budget.id}`} className="b2b-panel__row">
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-3">
                    <span className="font-medium truncate">{budget.number}</span>
                    <span className={cn('b2b-pill', config.pill)}>{config.label}</span>
                  </span>
                  <span className="block text-sm b2b-muted">{client?.name || 'Sin cliente'}</span>
                  <span className="block text-xs b2b-muted mt-1">{formatCurrency(budget.total)} · {formatRelativeTime(budget.createdAt)}</span>
                </span>
              </Link>
            );
          })
        )}
      </div>
    </div>
  );
}
